/**
 * useUpload Hook - Evidence file upload with per-file job tracking for the UI
 */
import { useState, useCallback } from 'react';
import { apiFetch } from '../services/api_service';

export function useUpload() {
  const [jobs, setJobs] = useState({});

  // Upload a file via the synchronous /v1/evidence/upload endpoint.
  // (The async pipeline /v1/async/upload/* was deleted 2026-07-21: its
  // background workers were never wired into the app and its event-bus
  // contract was descoped with Kafka.)
  const uploadFile = useCallback(async (file, options = {}) => {
    const { description, frameworkTags } = options;

    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (frameworkTags) formData.append('tags', frameworkTags);

    const data = await apiFetch(`/v1/evidence/upload`, {
      method: 'POST',
      body: formData
    });

    if (!data || data._error) {
      if (data?.status === 409) {
        return { isDuplicate: true, message: data.message };
      }
      throw new Error(data?.message || 'Upload failed');
    }

    // Synchronous upload — record an already-completed job for the UI
    const jobId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setJobs(prev => ({
      ...prev,
      [jobId]: {
        jobId,
        fileId: data.evidence_id || data.evidence?.evidence_id,
        fileName: file.name,
        fileSize: data.file_size || file.size,
        status: 'completed',
        progress: 100,
        message: 'Uploaded',
        result: null,
        error: null,
        startedAt: new Date()
      }
    }));

    return { ...data, jobId };
  }, []);

  // Clear completed jobs
  const clearCompleted = useCallback(() => {
    setJobs(prev => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        if (v.status !== 'completed' && v.status !== 'failed') {
          next[k] = v;
        }
      }
      return next;
    });
  }, []);

  const activeJobs = Object.values(jobs);
  const hasActiveJobs = activeJobs.some(j => j.status !== 'completed' && j.status !== 'failed');

  return {
    uploadFile,
    jobs: activeJobs,
    hasActiveJobs,
    clearCompleted
  };
}
